import type { FC } from 'react'
import { useEffect } from 'react'

import Checkbox from '@/components/Checkbox'
import CheckboxWithDisabledTooltip from '@/components/CheckboxWithDisabledTooltip'
import useUser from '@/hooks/useUser'

export interface Filter {
  onlyShowWorkingQueue: boolean
  includeAgentUploadedDocumentsToday: boolean
  includeApplicantUploadedDocumentsToday: boolean
}

export const DEFAULT_FILTER: Filter = {
  onlyShowWorkingQueue: true,
  includeAgentUploadedDocumentsToday: false,
  includeApplicantUploadedDocumentsToday: false,
}

interface Props {
  filter: Filter
  onChangeFilter: (filter: Filter) => void
  priorityProductSelected: boolean
}

const tooltipText =
  "Cannot disable this selection while Priority tier is selected and 'Just show today's queue' is enabled"

const QueueSettingsFilter: FC<Props> = ({
  filter,
  onChangeFilter,
  priorityProductSelected,
}) => {
  const { isDeveloperModeEnabled } = useUser()

  useEffect(() => {
    if (!isDeveloperModeEnabled) {
      if (!filter.onlyShowWorkingQueue) {
        onChangeFilter({
          ...filter,
          onlyShowWorkingQueue: true,
        })
        return
      }
    }

    if (
      priorityProductSelected &&
      filter.onlyShowWorkingQueue &&
      (!filter.includeAgentUploadedDocumentsToday ||
        !filter.includeApplicantUploadedDocumentsToday)
    ) {
      onChangeFilter({
        ...filter,
        includeAgentUploadedDocumentsToday: true,
        includeApplicantUploadedDocumentsToday: true,
      })
    }
  }, [
    isDeveloperModeEnabled,
    priorityProductSelected,
    filter,
    onChangeFilter,
    filter.onlyShowWorkingQueue,
  ])

  return (
    <>
      {isDeveloperModeEnabled && (
        <Checkbox
          label="Just show todays queue"
          checked={filter.onlyShowWorkingQueue}
          onChange={e => {
            onChangeFilter({
              ...filter,
              onlyShowWorkingQueue: e.target.checked,
              ...(e.target.checked
                ? {}
                : {
                    includeAgentUploadedDocumentsToday: false,
                    includeApplicantUploadedDocumentsToday: false,
                  }),
            })
          }}
        />
      )}
      {filter.onlyShowWorkingQueue && (
        <>
          <CheckboxWithDisabledTooltip
            label="Show agent uploaded documents"
            checked={filter.includeAgentUploadedDocumentsToday}
            disabled={priorityProductSelected || !filter.onlyShowWorkingQueue}
            disabledTooltip={tooltipText}
            onChange={e =>
              onChangeFilter({
                ...filter,
                includeAgentUploadedDocumentsToday: e.target.checked,
              })
            }
          />
          <CheckboxWithDisabledTooltip
            label="Show applicant uploaded documents"
            checked={filter.includeApplicantUploadedDocumentsToday}
            disabled={priorityProductSelected || !filter.onlyShowWorkingQueue}
            disabledTooltip={tooltipText}
            onChange={e =>
              onChangeFilter({
                ...filter,
                includeApplicantUploadedDocumentsToday: e.target.checked,
              })
            }
          />
        </>
      )}
    </>
  )
}

export default QueueSettingsFilter
